import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./AdminOrderList.css";

/* ══════════════════════════════════════════════════
   API CONFIG
══════════════════════════════════════════════════ */
const RAW_API_BASE = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || "";
const CLEAN_API_BASE = String(RAW_API_BASE || "").trim().replace(/\/+$/, "");
const API_URL = CLEAN_API_BASE
  ? CLEAN_API_BASE.endsWith("/api")
    ? CLEAN_API_BASE
    : `${CLEAN_API_BASE}/api`
  : "/api";

const ORDERS_API = `${API_URL}/reservations`;
const HEALTH_API = `${API_URL}/health`;
const ADMIN_API = `${API_URL}/admin`;

/* ══════════════════════════════════════════════════
   CONSTANTS
══════════════════════════════════════════════════ */
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const ADVANCE_PAID = 700;
const GST_RATE = 0.18;
const DEFAULT_FIXED_DECOR = 700;
const DEFAULT_EXTRA_ADULT_PRICE = 400;
const DEFAULT_KID_PRICE = 200;
const DEFAULT_3D_GLASS_PRICE = 300;
const ACTIVE_ORDERS_KEY = "hexaplay_orders";
const FROZEN_ORDERS_KEY = "hexaplay_frozen_orders";
const PERMANENT_LOCKS_KEY = "hexaplay_permanent_frozen_order_keys";

/* ══════════════════════════════════════════════════
   HELPERS
══════════════════════════════════════════════════ */
const n = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const money = (value) => `₹${n(value).toLocaleString("en-IN")}`;

// Captures qty regardless of where it came from
const safeQty = (item) => Math.max(1, Math.floor(n(item?.qty || item?.quantity || item?.count || 1)));
const itemTotal = (item) => n(item?.price) * safeQty(item);

const getOrderId = (order) =>
  order?._id || order?.id || order?.orderId || order?.bookingId || "";

const canUseLocalStorage = () =>
  typeof window !== "undefined" && typeof window.localStorage !== "undefined";

const readStoredArray = (key) => {
  if (!canUseLocalStorage()) return [];

  try {
    const parsed = JSON.parse(window.localStorage.getItem(key) || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeStoredArray = (key, value) => {
  if (!canUseLocalStorage()) return;

  try {
    window.localStorage.setItem(key, JSON.stringify(Array.isArray(value) ? value : []));
  } catch {}
};

const getOrderStorageKey = (order) => {
  const id = getOrderId(order);
  if (id) return String(id);

  return [order?.name, order?.whatsapp, order?.date, order?.timeSlot, order?.timestamp]
    .filter(Boolean)
    .map((part) => String(part).trim())
    .join("|");
};

const readFrozenKeys = () => new Set(readStoredArray(PERMANENT_LOCKS_KEY).filter(Boolean));

const writeFrozenKeys = (keys) => writeStoredArray(PERMANENT_LOCKS_KEY, [...keys].filter(Boolean));

const rememberFrozenLock = (order) => {
  const key = getOrderStorageKey(order);
  if (!key) return;

  const keys = readFrozenKeys();
  keys.add(key);
  writeFrozenKeys(keys);
};

const isOrderLocked = (order, knownKeys = null) => {
  if (!order) return false;
  if (order.frozen === true) return true;

  const key = getOrderStorageKey(order);
  if (!key) return false;

  return (knownKeys || readFrozenKeys()).has(key);
};

const toLockedOrder = (order) => ({
  ...order,
  frozen: true,
  frozenAt: order?.frozenAt || new Date().toISOString(),
  frozenBy: order?.frozenBy || "Admin",
  status: order?.paymentInfo ? "Fully Paid" : order?.status || "Advance Paid",
  total: n(order?.total || computeTotal(order)),
});

const upsertFrozenLocal = (order) => {
  const locked = toLockedOrder(order);
  rememberFrozenLock(locked);

  const rows = readStoredArray(FROZEN_ORDERS_KEY);
  const key = getOrderStorageKey(locked);
  const map = new Map(rows.map((o) => [getOrderStorageKey(o), o]).filter(([k]) => k));
  map.set(key, locked);
  writeStoredArray(FROZEN_ORDERS_KEY, [...map.values()]);
};

const removeActiveLocal = (order) => {
  const key = getOrderStorageKey(order);
  const rows = readStoredArray(ACTIVE_ORDERS_KEY);
  writeStoredArray(
    ACTIVE_ORDERS_KEY,
    rows.filter((o) => getOrderStorageKey(o) !== key)
  );
};

const formatTS = (ts) => {
  if (!ts) return "—";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const parseEventDate = (dateStr) => {
  if (!dateStr) return null;
  const parts = String(dateStr).split("/");
  if (parts.length !== 3) return null;
  const [dd, mm, yyyy] = parts.map(Number);
  if (!dd || !mm || !yyyy) return null;
  const d = new Date(yyyy, mm - 1, dd);
  return Number.isNaN(d.getTime()) ? null : d;
};

const toInputVal = (ddmmyyyy) => {
  if (!ddmmyyyy) return "";
  const p = String(ddmmyyyy).split("/");
  if (p.length !== 3) return "";
  return `${p[2]}-${p[1].padStart(2, "0")}-${p[0].padStart(2, "0")}`;
};

const fromInputVal = (yyyymmdd) => {
  if (!yyyymmdd) return "";
  const p = String(yyyymmdd).split("-");
  if (p.length !== 3) return "";
  return `${p[2]}/${p[1]}/${p[0]}`;
};

const formatRangeLabel = (from, to) => {
  const fmt = (s) => {
    if (!s) return "";
    const p = s.split("/");
    return `${p[0]} ${MONTH_NAMES[parseInt(p[1], 10) - 1] || ""} ${p[2]}`;
  };
  if (from && to) return `${fmt(from)} – ${fmt(to)}`;
  if (from) return `From ${fmt(from)}`;
  if (to) return `Until ${fmt(to)}`;
  return "All Dates";
};

const normalizeCatalogItem = (item) => ({
  id: item?._id || item?.id || item?.slug || item?.name,
  _id: item?._id,
  name: item?.name || item?.title || "Untitled",
  price: n(item?.price),
});

const getAddonArrays = (o) => ({
  cakes: Array.isArray(o?.cakes) ? o.cakes : [],
  extraDecors: Array.isArray(o?.extraDecors) ? o.extraDecors : [],
  gifts: Array.isArray(o?.gifts) ? o.gifts : [],
  services: Array.isArray(o?.services) ? o.services : [],
});

const computeSubtotalBeforeManualAdjustments = (o) => {
  const a = getAddonArrays(o);
  let total = n(o?.basePrice) + n(o?.fixedDecor || DEFAULT_FIXED_DECOR);

  total += Math.max(0, n(o?.extraAdults)) * n(o?.extraAdultPrice || DEFAULT_EXTRA_ADULT_PRICE);
  total += Math.max(0, n(o?.kids)) * n(o?.kidPrice || DEFAULT_KID_PRICE);

  total +=
    Math.max(0, n(o?.glassesCount)) *
    (n(o?.glass3DCost)
      ? n(o?.glass3DCost) / Math.max(1, n(o?.glassesCount))
      : DEFAULT_3D_GLASS_PRICE);

  a.cakes.forEach((x) => (total += itemTotal(x)));
  a.extraDecors.forEach((x) => (total += itemTotal(x)));
  a.gifts.forEach((x) => (total += itemTotal(x)));
  a.services.forEach((x) => (total += itemTotal(x)));

  return total;
};

const computeTotal = (o) => {
  const base = computeSubtotalBeforeManualAdjustments(o);
  const foodCost = n(o?.foodCost || o?.paymentInfo?.foodCost);
  const manualDiscount = n(o?.manualDiscount || o?.paymentInfo?.manualDiscount);
  const couponDiscount = n(o?.discount);
  return Math.max(0, base + foodCost - manualDiscount - couponDiscount);
};

const addonText = (o) => {
  const a = getAddonArrays(o);
  const items = [
    ...a.cakes.map((x) => `🎂 ${x.name}${safeQty(x) > 1 ? ` x${safeQty(x)}` : ""} (${money(itemTotal(x))})`),
    ...a.extraDecors.map((x) => `🎨 ${x.name}${safeQty(x) > 1 ? ` x${safeQty(x)}` : ""} (${money(itemTotal(x))})`),
    ...a.gifts.map((x) => `🎁 ${x.name}${safeQty(x) > 1 ? ` x${safeQty(x)}` : ""} (${money(itemTotal(x))})`),
    ...a.services.map((x) => `🛎️ ${x.name}${safeQty(x) > 1 ? ` x${safeQty(x)}` : ""} (${money(itemTotal(x))})`),
  ];

  if (n(o?.glassesCount) > 0) {
    items.push(`🥽 3D Glasses x${n(o.glassesCount)} (${money(o.glass3DCost)})`);
  }

  return items.join(", ") || "—";
};

const buildAddonRows = (o) => {
  const a = getAddonArrays(o);

  const rows = [
    ...a.cakes.map((x) => ({ ...x, emoji: "🎂", category: "Cake" })),
    ...a.extraDecors.map((x) => ({ ...x, emoji: "🎨", category: "Decor" })),
    ...a.gifts.map((x) => ({ ...x, emoji: "🎁", category: "Gift" })),
    ...a.services.map((x) => ({ ...x, emoji: "🛎️", category: "Service" })),
  ];

  if (n(o?.glassesCount) > 0) {
    rows.push({
      name: "3D Glasses",
      price: DEFAULT_3D_GLASS_PRICE,
      qty: n(o.glassesCount),
      emoji: "🥽",
      category: "Accessory",
    });
  }

  return rows;
};

const escapeHTML = (str) =>
  String(str ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");

/* ══════════════════════════════════════════════════
   WHATSAPP MESSAGE
══════════════════════════════════════════════════ */
const buildWAMessage = (o) => {
  const addonLines = buildAddonRows(o).map(
    (x) => `${x.emoji} ${x.name}${safeQty(x) > 1 ? ` x${safeQty(x)}` : ""} — ${money(itemTotal(x))}`
  );

  const total = n(o.total || computeTotal(o));
  const advance = n(o.advancePaid || ADVANCE_PAID);
  const balance = Math.max(0, total - advance);

  return encodeURIComponent(
    [
      "🎬 HexaPlay – Booking Confirmation",
      "━━━━━━━━━━━━━━━━━━━━",
      "",
      "👤 Customer",
      `Name: ${o.name || "—"}`,
      `Celebrating: ${o.celebrationName || "—"}`,
      `WhatsApp: ${o.whatsapp || "—"}`,
      `Email: ${o.email || "N/A"}`,
      "",
      "🏟️ Booking",
      `Theater: ${o.theater || "—"}`,
      `Date: ${o.date || "—"}`,
      `Time Slot: ${o.timeSlot || "—"}`,
      `Occasion: ${o.occasion || "—"}`,
      "",
      `👥 Guests: ${n(o.adults)} Adults${n(o.kids) > 0 ? `, ${n(o.kids)} Kids` : ""}`,
      ...(n(o.glassesCount) > 0 ? [`🥽 3D Glasses: ${n(o.glassesCount)}`] : []),
      "",
      "💵 Pricing",
      `Grand Total: ${money(total)}`,
      `Advance Paid: ${money(advance)}`,
      `Balance Due: ${money(balance)}`,
      ...(addonLines.length ? ["", "🛍️ Add-ons:", ...addonLines] : []),
      "",
      "Thank you for choosing HexaPlay! 🎉",
    ].join("\n")
  );
};

/* ══════════════════════════════════════════════════
   PRINT SINGLE RECEIPT
══════════════════════════════════════════════════ */
const printRowPDF = (o) => {
  const total = n(o.total || computeTotal(o));
  const advance = n(o.advancePaid || ADVANCE_PAID);
  const balance = Math.max(0, total - advance);
  const addons = buildAddonRows(o);
  const paymentInfo = o.paymentInfo;

  const addonRows = addons.length
    ? addons
        .map(
          (a) =>
            `<tr>
              <td>${escapeHTML(a.emoji)} ${escapeHTML(a.name)}${safeQty(a) > 1 ? ` <span class="qty">x${safeQty(a)}</span>` : ""}</td>
              <td class="amt">${money(itemTotal(a))}</td>
            </tr>`
        )
        .join("")
    : `<tr><td colspan="2" class="muted">No add-ons selected</td></tr>`;

  const paymentSection = paymentInfo
    ? `<div class="section full">
        <div class="section-title">💳 Payment Settlement Details</div>
        <table class="price-table">
          <tr>
            <td>Grand Total</td>
            <td class="amt">${money(paymentInfo.grandTotal ?? total)}</td>
          </tr>
          <tr>
            <td>Advance Paid</td>
            <td class="amt">-${money(paymentInfo.advancePaid ?? advance)}</td>
          </tr>
          <tr>
            <td>Actual Balance</td>
            <td class="amt">${money(paymentInfo.actualBalance ?? balance)}</td>
          </tr>
          <tr>
            <td>Food Cost</td>
            <td class="amt">+${money(paymentInfo.foodCost || 0)}</td>
          </tr>
          <tr>
            <td>Less Discount</td>
            <td class="amt">-${money(paymentInfo.manualDiscount || 0)}</td>
          </tr>
          <tr>
            <td>Settlement Balance</td>
            <td class="amt">${money(paymentInfo.settlementBalance ?? balance)}</td>
          </tr>
          <tr>
            <td>Payment Mode</td>
            <td class="amt">
              ${
                (paymentInfo.modes || [])
                  .map((m) => `${escapeHTML(m.mode)} ${money(m.amount)}`)
                  .join(" + ") || "—"
              }
            </td>
          </tr>
          <tr>
            <td>Payment Collected</td>
            <td class="amt">${money(paymentInfo.totalPaid || 0)}</td>
          </tr>
          ${
            paymentInfo.gstApplied
              ? `<tr>
                  <td>GST Number</td>
                  <td class="amt">${escapeHTML(paymentInfo.gstNumber || "N/A")}</td>
                </tr>
                <tr>
                  <td>GST 18%</td>
                  <td class="amt">+${money(paymentInfo.gstAmount || 0)}</td>
                </tr>`
              : `<tr>
                  <td>GST</td>
                  <td class="amt">Not Applied</td>
                </tr>`
          }
          <tr class="total-row">
            <td>Final Amount Paid</td>
            <td class="amt">${money(paymentInfo.finalAmount || 0)}</td>
          </tr>
          <tr>
            <td>Remaining Due</td>
            <td class="amt">${money(paymentInfo.remainingDue || 0)}</td>
          </tr>
          <tr>
            <td>Paid At</td>
            <td class="amt">${escapeHTML(formatTS(paymentInfo.paidAt))}</td>
          </tr>
        </table>
      </div>`
    : `<div class="section full">
        <div class="section-title">💳 Payment Settlement Details</div>
        <table class="price-table">
          <tr><td colspan="2" class="muted">Payment settlement not confirmed yet</td></tr>
        </table>
      </div>`;

  const html = `<!DOCTYPE html>
  <html>
  <head>
    <meta charset="UTF-8"/>
    <title>HexaPlay Receipt</title>
    <style>
      *{box-sizing:border-box}
      body{font-family:Arial,sans-serif;margin:0;color:#0f172a;background:#fff;font-size:12px}
      .page{max-width:780px;margin:0 auto}
      .hdr{padding:30px 38px;background:linear-gradient(135deg,#18115f,#2563eb);color:#fff;display:flex;justify-content:space-between}
      .hdr h1{margin:0;font-size:25px}
      .hdr p{margin:4px 0 0;opacity:.8}
      .body{padding:26px 38px}
      .grid{display:grid;grid-template-columns:1fr 1fr;gap:18px}
      .section{margin-bottom:18px}
      .section.full{grid-column:1/-1}
      .section-title{font-weight:800;color:#475569;text-transform:uppercase;letter-spacing:.08em;font-size:10px;margin-bottom:9px}
      .info,.price-table{width:100%;border-collapse:collapse;border:1px solid #e5e7eb;border-radius:12px;overflow:hidden}
      .info td,.price-table td{padding:9px 12px;border-bottom:1px solid #eef2f7}
      .info tr:last-child td,.price-table tr:last-child td{border-bottom:0}
      .lbl{background:#f8fafc;color:#64748b;width:42%;font-weight:600}
      .val{font-weight:700}
      .amt{text-align:right;font-weight:800}
      .muted{text-align:center;color:#94a3b8}
      .qty{color:#64748b}
      .total-row td{background:#111827;color:#fff}
      .advance td{background:#ecfdf5;color:#15803d;font-weight:800}
      .footer{padding:16px 38px;background:#f8fafc;border-top:1px solid #e5e7eb;display:flex;justify-content:space-between;color:#64748b}
      .badge{display:inline-block;padding:5px 10px;border-radius:99px;background:#dcfce7;color:#15803d;font-weight:800}
      .thanks{text-align:center;margin-top:16px;padding:14px;border-radius:12px;background:#eff6ff;color:#1d4ed8;font-weight:800}
      @media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
    </style>
  </head>
  <body>
    <div class="page">
      <div class="hdr">
        <div>
          <h1>🎬 HexaPlay</h1>
          <p>Official Booking Receipt</p>
        </div>
        <div style="text-align:right">
          <div>${getOrderId(o) ? `ID: ${escapeHTML(getOrderId(o))}` : "Booking Receipt"}</div>
          <p>${new Date().toLocaleDateString("en-IN")}</p>
          <span class="badge">${escapeHTML(isOrderLocked(o) ? "Frozen" : o.status || "Advance Paid")}</span>
        </div>
      </div>

      <div class="body">
        <div class="grid">
          <div class="section">
            <div class="section-title">Customer</div>
            <table class="info">
              <tr><td class="lbl">Name</td><td class="val">${escapeHTML(o.name || "—")}</td></tr>
              <tr><td class="lbl">Celebrant</td><td class="val">${escapeHTML(o.celebrationName || "—")}</td></tr>
              <tr><td class="lbl">WhatsApp</td><td class="val">${escapeHTML(o.whatsapp || "—")}</td></tr>
              <tr><td class="lbl">Email</td><td class="val">${escapeHTML(o.email || "—")}</td></tr>
            </table>
          </div>

          <div class="section">
            <div class="section-title">Booking</div>
            <table class="info">
              <tr><td class="lbl">Theater</td><td class="val">${escapeHTML(o.theater || "—")}</td></tr>
              <tr><td class="lbl">Date</td><td class="val">${escapeHTML(o.date || "—")}</td></tr>
              <tr><td class="lbl">Slot</td><td class="val">${escapeHTML(o.timeSlot || "—")}</td></tr>
              <tr><td class="lbl">Occasion</td><td class="val">${escapeHTML(o.occasion || "—")}</td></tr>
            </table>
          </div>

          <div class="section">
            <div class="section-title">Pricing</div>
            <table class="price-table">
              <tr><td>Base Price</td><td class="amt">${money(o.basePrice)}</td></tr>
              <tr><td>Fixed Decor</td><td class="amt">${money(o.fixedDecor || DEFAULT_FIXED_DECOR)}</td></tr>
              ${
                n(o.extraAdults) > 0
                  ? `<tr><td>Extra Adults (${n(o.extraAdults)})</td><td class="amt">${money(n(o.extraAdults) * n(o.extraAdultPrice || DEFAULT_EXTRA_ADULT_PRICE))}</td></tr>`
                  : ""
              }
              ${
                n(o.kids) > 0
                  ? `<tr><td>Kids (${n(o.kids)})</td><td class="amt">${money(n(o.kids) * n(o.kidPrice || DEFAULT_KID_PRICE))}</td></tr>`
                  : ""
              }
              ${
                n(o.discount) > 0
                  ? `<tr><td>Coupon Discount</td><td class="amt">-${money(o.discount)}</td></tr>`
                  : ""
              }
              <tr class="total-row"><td>Grand Total</td><td class="amt">${money(total)}</td></tr>
              <tr class="advance"><td>Advance Paid</td><td class="amt">${money(advance)}</td></tr>
              <tr><td>Balance Due</td><td class="amt">${money(balance)}</td></tr>
            </table>
          </div>

          <div class="section">
            <div class="section-title">Add-ons</div>
            <table class="price-table">${addonRows}</table>
          </div>

          ${paymentSection}
        </div>

        <div class="thanks">🎉 Thank you for choosing HexaPlay!</div>
      </div>

      <div class="footer">
        <span>HexaPlay Private Theater</span>
        <span>Total ${money(total)} · Advance ${money(advance)}</span>
      </div>
    </div>
  </body>
  </html>`;

  const iframe = document.createElement("iframe");
  iframe.style.cssText =
    "position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;border:none;";
  document.body.appendChild(iframe);
  iframe.contentDocument.open();
  iframe.contentDocument.write(html);
  iframe.contentDocument.close();

  setTimeout(() => {
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
    setTimeout(() => document.body.removeChild(iframe), 2000);
  }, 500);
};

/* ══════════════════════════════════════════════════
   BULK PDF
══════════════════════════════════════════════════ */
const downloadPDF = (rows, filterLabel) => {
  const totalAdvance = rows.reduce((a, o) => a + n(o.advancePaid || ADVANCE_PAID), 0);
  const totalGrand = rows.reduce((a, o) => a + n(o.total || computeTotal(o)), 0);
  const totalSettlement = rows.reduce((a, o) => a + n(o.paymentInfo?.totalPaid || 0), 0);

  const tableRows = rows
    .map(
      (o, i) => `<tr>
        <td>${i + 1}</td>
        <td><b>${escapeHTML(o.name || "—")}</b><br/><span>${escapeHTML(o.whatsapp || "—")}</span></td>
        <td>${escapeHTML(o.theater || "—")}</td>
        <td>${escapeHTML(o.date || "—")}<br/><span>${escapeHTML(o.timeSlot || "—")}</span></td>
        <td>${escapeHTML(o.occasion || "—")}</td>
        <td>${n(o.adults)}A${n(o.kids) ? ` / ${n(o.kids)}K` : ""}</td>
        <td>${escapeHTML(addonText(o))}</td>
        <td><b>${money(o.total || computeTotal(o))}</b><br/><span>${money(o.advancePaid || ADVANCE_PAID)} advance</span></td>
        <td>${o.paymentInfo ? escapeHTML((o.paymentInfo.modes || []).map((m) => `${m.mode} ${money(m.amount)}`).join(" + ")) : "—"}</td>
      </tr>`
    )
    .join("");

  const html = `<!DOCTYPE html>
  <html>
  <head>
    <meta charset="UTF-8"/>
    <title>HexaPlay Orders</title>
    <style>
      *{box-sizing:border-box}
      body{font-family:Arial,sans-serif;margin:0;color:#111827;font-size:11px}
      .cover{padding:28px 34px;background:linear-gradient(135deg,#18115f,#2563eb);color:#fff}
      h1{margin:0}
      table{width:100%;border-collapse:collapse}
      th{background:#f8fafc;color:#64748b;text-transform:uppercase;font-size:9px;padding:10px;border-bottom:2px solid #e5e7eb;text-align:left}
      td{padding:9px 10px;border-bottom:1px solid #eef2f7;vertical-align:top}
      span{color:#64748b;font-size:10px}
      .footer{padding:14px 24px;background:#f8fafc;display:flex;justify-content:space-between}
      @media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
    </style>
  </head>
  <body>
    <div class="cover"><h1>🎬 HexaPlay Orders</h1><p>${escapeHTML(filterLabel)}</p></div>
    <table>
      <thead>
        <tr>
          <th>#</th><th>Customer</th><th>Theater</th><th>Date / Slot</th><th>Occasion</th>
          <th>Guests</th><th>Add-ons</th><th>Amount</th><th>Settlement Mode</th>
        </tr>
      </thead>
      <tbody>${tableRows}</tbody>
    </table>
    <div class="footer">
      <b>${rows.length} bookings</b>
      <b>Advance ${money(totalAdvance)} · Total ${money(totalGrand)} · Settlement ${money(totalSettlement)}</b>
    </div>
  </body>
  </html>`;

  const iframe = document.createElement("iframe");
  iframe.style.cssText =
    "position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;border:none;";
  document.body.appendChild(iframe);
  iframe.contentDocument.open();
  iframe.contentDocument.write(html);
  iframe.contentDocument.close();

  setTimeout(() => {
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
    setTimeout(() => document.body.removeChild(iframe), 2000);
  }, 500);
};

/* ══════════════════════════════════════════════════
   ADD ADDONS MODAL
══════════════════════════════════════════════════ */
const AddAddonsModal = ({ order, onClose, onSave }) => {
  const categories = [
    { key: "cakes", label: "Cakes", emoji: "🎂", field: "cakes" },
    { key: "decorations", label: "Decorations", emoji: "🎨", field: "extraDecors" },
    { key: "gifts", label: "Gifts", emoji: "🎁", field: "gifts" },
    { key: "services", label: "Services", emoji: "🛎️", field: "services" },
  ];

  const [catalog, setCatalog] = useState({
    cakes: [],
    decorations: [],
    gifts: [],
    services: [],
  });
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [activeCategory, setActiveCategory] = useState("cakes");
  const [searchQuery, setSearchQuery] = useState("");
  const [saving, setSaving] = useState(false);

  const [selected, setSelected] = useState({
    cakes: [...(order.cakes || [])],
    extraDecors: [...(order.extraDecors || [])],
    gifts: [...(order.gifts || [])].map((g) => ({ ...g, qty: safeQty(g) })),
    services: [...(order.services || [])],
  });

  useEffect(() => {
    let ignore = false;

    const fetchCatalog = async () => {
      setLoadingCatalog(true);
      try {
        const results = await Promise.all(
          categories.map((c) =>
            fetch(`${ADMIN_API}/${c.key}`, { credentials: "include" })
              .then((r) => (r.ok ? r.json() : []))
              .catch(() => [])
          )
        );

        if (ignore) return;

        const next = {};
        categories.forEach((c, i) => {
          const raw = Array.isArray(results[i])
            ? results[i]
            : Array.isArray(results[i]?.data)
            ? results[i].data
            : [];
          next[c.key] = raw.map(normalizeCatalogItem);
        });

        setCatalog(next);
      } finally {
        if (!ignore) setLoadingCatalog(false);
      }
    };

    fetchCatalog();

    return () => {
      ignore = true;
    };
  }, []);

  const active = categories.find((c) => c.key === activeCategory) || categories[0];

  const suggestions = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const list = catalog[activeCategory] || [];
    return (q ? list.filter((x) => x.name.toLowerCase().includes(q)) : list).slice(0, 20);
  }, [catalog, activeCategory, searchQuery]);

  const isSelected = (field, item) =>
    (selected[field] || []).some(
      (x) => (x._id && item._id && x._id === item._id) || x.name === item.name
    );

  const toggleAddon = (catKey, item) => {
    const cat = categories.find((c) => c.key === catKey) || categories[0];
    const field = cat.field;

    setSelected((prev) => {
      const list = prev[field] || [];
      const exists = list.some(
        (x) => (x._id && item._id && x._id === item._id) || x.name === item.name
      );

      if (exists) {
        return {
          ...prev,
          [field]: list.filter((x) => x.name !== item.name && x._id !== item._id),
        };
      }

      const payload = {
        id: item.id,
        _id: item._id,
        name: item.name,
        price: n(item.price),
        ...(field === "gifts" ? { qty: 1 } : {}),
      };

      return { ...prev, [field]: [...list, payload] };
    });

    setSearchQuery("");
  };

  const changeQty = (field, name, delta) => {
    setSelected((prev) => {
      const list = prev[field] || [];

      return {
        ...prev,
        [field]: list
          .map((x) =>
            x.name === name ? { ...x, qty: Math.max(0, safeQty(x) + delta) } : x
          )
          .filter((x) => safeQty(x) > 0),
      };
    });
  };

  const removeAddon = (field, name) => {
    setSelected((prev) => ({
      ...prev,
      [field]: (prev[field] || []).filter((x) => x.name !== name),
    }));
  };

  const previewOrder = {
    ...order,
    cakes: selected.cakes,
    extraDecors: selected.extraDecors,
    gifts: selected.gifts,
    services: selected.services,
  };

  const newTotal = computeTotal(previewOrder);

  const allSelected = [
    ...selected.cakes.map((x) => ({ ...x, field: "cakes", emoji: "🎂" })),
    ...selected.extraDecors.map((x) => ({ ...x, field: "extraDecors", emoji: "🎨" })),
    ...selected.gifts.map((x) => ({ ...x, field: "gifts", emoji: "🎁" })),
    ...selected.services.map((x) => ({ ...x, field: "services", emoji: "🛎️" })),
  ];

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({ ...previewOrder, total: newTotal });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="aol-modal-overlay" onClick={onClose}>
      <div className="aol-addon-modal" onClick={(e) => e.stopPropagation()}>
        <div className="aol-addon-modal-header">
          <div className="aol-modal-title-block">
            <span className="aol-big-icon">🛍️</span>
            <div>
              <h2>Add / Edit Add-ons</h2>
              <p>
                {order.name} · {order.theater} · {order.date}
              </p>
            </div>
          </div>
          <button className="aol-icon-close" onClick={onClose}>✕</button>
        </div>

        <div className="aol-addon-modal-body">
          <div className="aol-addon-left-panel">
            <div className="aol-addon-cat-tabs">
              {categories.map((cat) => (
                <button
                  key={cat.key}
                  className={`aol-addon-cat-tab ${activeCategory === cat.key ? "active" : ""}`}
                  onClick={() => {
                    setActiveCategory(cat.key);
                    setSearchQuery("");
                  }}
                >
                  <span>{cat.emoji}</span>
                  <span>{cat.label}</span>
                  {selected[cat.field]?.length > 0 && <b>{selected[cat.field].length}</b>}
                </button>
              ))}
            </div>

            <div className="aol-addon-search-wrap">
              <span>🔍</span>
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={`Search ${active.label}...`}
                autoFocus
              />
              {searchQuery && <button onClick={() => setSearchQuery("")}>✕</button>}
            </div>

            <div className="aol-addon-suggestions">
              {loadingCatalog ? (
                <div className="aol-mini-state">Loading catalog...</div>
              ) : suggestions.length === 0 ? (
                <div className="aol-mini-state">No item found</div>
              ) : (
                suggestions.map((item) => {
                  const picked = isSelected(active.field, item);

                  return (
                    <button
                      key={item._id || item.name}
                      className={`aol-addon-suggestion-item ${picked ? "selected" : ""}`}
                      onClick={() => toggleAddon(activeCategory, item)}
                    >
                      <span className="aol-suggestion-main">
                        <b>{active.emoji}</b>
                        {item.name}
                      </span>
                      <span className="aol-suggestion-price">
                        {money(item.price)} <i>{picked ? "✓" : "+"}</i>
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          <div className="aol-addon-right-panel">
            <div className="aol-panel-head">
              <h3>Selected Items</h3>
              <span>
                {allSelected.length} item{allSelected.length !== 1 ? "s" : ""}
              </span>
            </div>

            <div className="aol-addon-selected-list">
              {allSelected.length === 0 ? (
                <div className="aol-empty-mini">
                  🛒<p>No add-ons selected yet</p>
                </div>
              ) : (
                allSelected.map((a) => (
                  <div key={`${a.field}-${a.name}`} className="aol-addon-selected-item">
                    <div>
                      <strong>
                        {a.emoji} {a.name}{safeQty(a) > 1 ? ` x${safeQty(a)}` : ""}
                      </strong>
                      <small>
                        {money(a.price)}
                        {a.field === "gifts" &&
                          ` x${safeQty(a)} = ${money(itemTotal(a))}`}
                      </small>
                    </div>

                    <div className="aol-selected-actions">
                      {a.field === "gifts" && (
                        <div className="aol-gift-qty-admin">
                          <button onClick={() => changeQty(a.field, a.name, -1)}>−</button>
                          <b>{safeQty(a)}</b>
                          <button onClick={() => changeQty(a.field, a.name, 1)}>+</button>
                        </div>
                      )}
                      <button
                        className="aol-remove-small"
                        onClick={() => removeAddon(a.field, a.name)}
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="aol-addon-price-breakup">
              <h4>Live Price List</h4>

              <div>
                <span>Base + Decor + Guests</span>
                <b>
                  {money(
                    computeSubtotalBeforeManualAdjustments({
                      ...previewOrder,
                      cakes: [],
                      extraDecors: [],
                      gifts: [],
                      services: [],
                    })
                  )}
                </b>
              </div>

              {allSelected.map((a) => (
                <div key={`price-${a.field}-${a.name}`}>
                  <span>
                    {a.emoji} {a.name}{safeQty(a) > 1 ? ` x${safeQty(a)}` : ""}
                  </span>
                  <b>{money(itemTotal(a))}</b>
                </div>
              ))}

              {n(order.discount) > 0 && (
                <div className="negative">
                  <span>Coupon Discount</span>
                  <b>-{money(order.discount)}</b>
                </div>
              )}

              <div className="total">
                <span>New Grand Total</span>
                <b>{money(newTotal)}</b>
              </div>

              <div className="balance">
                <span>Balance Due</span>
                <b>{money(Math.max(0, newTotal - n(order.advancePaid || ADVANCE_PAID)))}</b>
              </div>
            </div>
          </div>
        </div>

        <div className="aol-addon-modal-footer">
          <strong>New Total: {money(newTotal)}</strong>
          <div>
            <button className="aol-light-btn" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button className="aol-primary-btn" onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "💾 Save Add-ons"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════
   PAYMENT SETTLEMENT MODAL
══════════════════════════════════════════════════ */
const PayNowModal = ({ order, onClose, onSubmit }) => {
  const grandTotal = n(order.total || computeTotal(order));
  const advance = n(order.advancePaid || ADVANCE_PAID);
  const actualBalance = Math.max(0, grandTotal - advance);

  const existingModes = order.paymentInfo?.modes || [];

  const buildInitialModes = () => {
    const base = {
      cash: { enabled: false, amount: "" },
      card: { enabled: false, amount: "" },
      upi: { enabled: false, amount: "" },
      bank: { enabled: false, amount: "" },
    };

    existingModes.forEach((m) => {
      const key = String(m.mode || "").toLowerCase();
      if (base[key]) {
        base[key] = { enabled: true, amount: n(m.amount) };
      }
    });

    return base;
  };

  const [foodCost, setFoodCost] = useState(n(order.foodCost || order.paymentInfo?.foodCost));
  const [manualDiscount, setManualDiscount] = useState(
    n(order.manualDiscount || order.paymentInfo?.manualDiscount)
  );
  const [paymentModes, setPaymentModes] = useState(buildInitialModes);
  const [applyGST, setApplyGST] = useState(Boolean(order.paymentInfo?.gstApplied));
  const [gstNumber, setGstNumber] = useState(order.paymentInfo?.gstNumber || "");
  const [submitting, setSubmitting] = useState(false);

  const settlementBalance = Math.max(0, actualBalance + n(foodCost) - n(manualDiscount));

  const totalPaid = Object.values(paymentModes).reduce(
    (sum, mode) => sum + (mode.enabled ? n(mode.amount) : 0),
    0
  );

  const gstAmount = applyGST ? Math.round(totalPaid * GST_RATE) : 0;
  const finalAmount = totalPaid + gstAmount;
  const remainingDue = Math.max(0, settlementBalance - totalPaid);
  const overPaid = Math.max(0, totalPaid - settlementBalance);

  const progress =
    settlementBalance > 0
      ? Math.min(100, Math.round((totalPaid / settlementBalance) * 100))
      : 100;

  const modeConfig = {
    cash: { icon: "💵", label: "Cash", color: "#16a34a" },
    card: { icon: "💳", label: "Card", color: "#2563eb" },
    upi: { icon: "📱", label: "UPI", color: "#7c3aed" },
    bank: { icon: "🏦", label: "Bank", color: "#f97316" },
  };

  const distributeRemaining = (targetMode) => {
    const paidOther = Object.entries(paymentModes).reduce(
      (sum, [key, mode]) =>
        key === targetMode ? sum : sum + (mode.enabled ? n(mode.amount) : 0),
      0
    );

    return Math.max(0, settlementBalance - paidOther);
  };

  const toggleMode = (mode) => {
    setPaymentModes((prev) => {
      const willEnable = !prev[mode].enabled;

      return {
        ...prev,
        [mode]: {
          enabled: willEnable,
          amount: willEnable ? distributeRemaining(mode) || "" : "",
        },
      };
    });
  };

  const updateModeAmount = (mode, value) => {
    setPaymentModes((prev) => ({
      ...prev,
      [mode]: {
        ...prev[mode],
        amount: value === "" ? "" : Math.max(0, n(value)),
      },
    }));
  };

  const clearModes = () => {
    setPaymentModes({
      cash: { enabled: false, amount: "" },
      card: { enabled: false, amount: "" },
      upi: { enabled: false, amount: "" },
      bank: { enabled: false, amount: "" },
    });
  };

  const autoSplit = () => {
    const enabled = Object.entries(paymentModes)
      .filter(([, m]) => m.enabled)
      .map(([k]) => k);

    if (enabled.length === 0) {
      alert("Please select payment mode first.");
      return;
    }

    const each = Math.floor(settlementBalance / enabled.length);
    const remainder = settlementBalance - each * enabled.length;

    setPaymentModes((prev) => {
      const next = { ...prev };

      enabled.forEach((key, idx) => {
        next[key] = {
          ...next[key],
          amount: each + (idx === 0 ? remainder : 0),
        };
      });

      return next;
    });
  };

  const handleSubmit = async () => {
    if (totalPaid <= 0) {
      alert("Please collect amount in at least one payment mode.");
      return;
    }

    if (remainingDue > 0) {
      alert(`Balance due is still ${money(remainingDue)}. Please complete payment before confirm.`);
      return;
    }

    if (applyGST && !gstNumber.trim()) {
      alert("Please enter GST number or select No GST.");
      return;
    }

    setSubmitting(true);

    try {
      const modes = Object.entries(paymentModes)
        .filter(([, mode]) => mode.enabled && n(mode.amount) > 0)
        .map(([mode, data]) => ({
          mode: mode.toUpperCase(),
          amount: n(data.amount),
        }));

      await onSubmit({
        modes,
        grandTotal,
        advancePaid: advance,
        actualBalance,
        foodCost: n(foodCost),
        manualDiscount: n(manualDiscount),
        settlementBalance,
        totalPaid,
        remainingDue,
        overPaid,
        gstApplied: applyGST,
        gstNumber: applyGST ? gstNumber.trim().toUpperCase() : null,
        gstAmount,
        finalAmount,
        paidAt: new Date().toISOString(),
      });

      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="aol-modal-overlay" onClick={onClose}>
      <div className="aol-paynow-modal" onClick={(e) => e.stopPropagation()}>
        <div className="aol-paynow-hero">
          <div className="aol-modal-title-block">
            <span className="aol-big-icon">💳</span>
            <div>
              <h2>Payment Settlement</h2>
              <p>
                {order.name} · {order.theater} · {order.date}
              </p>
            </div>
          </div>

          <button className="aol-icon-close" onClick={onClose}>✕</button>
        </div>

        <div className="aol-paynow-body">
          <div className="aol-pay-summary-grid">
            <div>
              <span>Grand Total</span>
              <b>{money(grandTotal)}</b>
            </div>
            <div>
              <span>Advance Paid</span>
              <b>{money(advance)}</b>
            </div>
            <div className="due">
              <span>Actual Balance</span>
              <b>{money(actualBalance)}</b>
            </div>
          </div>

          <div className="aol-adjustment-card">
            <div className="aol-section-heading">
              <h3>Food Cost & Less Discount</h3>
              <p>Add food cost and reduce discount before final settlement.</p>
            </div>

            <div className="aol-adjustment-grid">
              <label>
                <span>🍽️ Food Cost</span>
                <div>
                  <i>₹</i>
                  <input
                    type="number"
                    min="0"
                    value={foodCost}
                    onChange={(e) => setFoodCost(e.target.value)}
                    placeholder="0"
                  />
                </div>
              </label>

              <label>
                <span>🏷️ Less Discount Price</span>
                <div>
                  <i>₹</i>
                  <input
                    type="number"
                    min="0"
                    value={manualDiscount}
                    onChange={(e) => setManualDiscount(e.target.value)}
                    placeholder="0"
                  />
                </div>
              </label>
            </div>

            <div className="aol-settlement-balance">
              <span>Settlement Balance = Actual Balance + Food Cost − Less Discount</span>
              <strong>{money(settlementBalance)}</strong>
            </div>
          </div>

          <div className="aol-paynow-section">
            <div className="aol-section-heading horizontal">
              <div>
                <h3>Payment Mode</h3>
                <p>Select one or multiple payment modes.</p>
              </div>

              <div className="aol-mode-tools">
                <button onClick={autoSplit}>Auto Split</button>
                <button onClick={clearModes}>Clear</button>
              </div>
            </div>

            <div className="aol-live-balance-card">
              <div className="aol-progress-top">
                <span>Collected {money(totalPaid)}</span>
                <b>{progress}%</b>
              </div>

              <div className="aol-progress">
                <span style={{ width: `${progress}%` }} />
              </div>

              <div className="aol-progress-bottom">
                <span>
                  Balance Due:{" "}
                  <b className={remainingDue > 0 ? "danger" : "ok"}>
                    {money(remainingDue)}
                  </b>
                </span>
                {overPaid > 0 && (
                  <span>
                    Extra: <b>{money(overPaid)}</b>
                  </span>
                )}
              </div>
            </div>

            <div className="aol-payment-mode-grid">
              {Object.entries(modeConfig).map(([mode, cfg]) => {
                const active = paymentModes[mode].enabled;

                return (
                  <div
                    key={mode}
                    className={`aol-mode-card ${active ? "active" : ""}`}
                    style={{ "--mode-color": cfg.color }}
                  >
                    <label className="aol-mode-check">
                      <input
                        type="checkbox"
                        checked={active}
                        onChange={() => toggleMode(mode)}
                      />
                      <span>{cfg.icon}</span>
                      <b>{cfg.label}</b>
                    </label>

                    {active && (
                      <div className="aol-mode-amount">
                        <i>₹</i>
                        <input
                          type="number"
                          min="0"
                          step="10"
                          value={paymentModes[mode].amount}
                          onChange={(e) => updateModeAmount(mode, e.target.value)}
                          placeholder="0"
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="aol-gst-card">
            <div className="aol-section-heading">
              <h3>GST Details</h3>
              <p>Optional GST calculation on collected amount.</p>
            </div>

            <div className="aol-gst-options">
              <label className={!applyGST ? "selected" : ""}>
                <input
                  type="radio"
                  checked={!applyGST}
                  onChange={() => {
                    setApplyGST(false);
                    setGstNumber("");
                  }}
                />
                🚫 No GST
              </label>

              <label className={applyGST ? "selected" : ""}>
                <input
                  type="radio"
                  checked={applyGST}
                  onChange={() => setApplyGST(true)}
                />
                🧾 Apply GST 18%
              </label>
            </div>

            {applyGST && (
              <input
                className="aol-gst-input"
                value={gstNumber}
                onChange={(e) => setGstNumber(e.target.value.toUpperCase())}
                maxLength="15"
                placeholder="GST Number e.g. 27AABCT1234H1Z0"
              />
            )}
          </div>

          <div className="aol-final-calc">
            <h3>Final Calculation</h3>

            <div>
              <span>Grand Total</span>
              <b>{money(grandTotal)}</b>
            </div>

            <div className="negative">
              <span>− Advance Paid</span>
              <b>−{money(advance)}</b>
            </div>

            <div>
              <span>Actual Balance</span>
              <b>{money(actualBalance)}</b>
            </div>

            <div>
              <span>+ Food Cost</span>
              <b>+{money(foodCost)}</b>
            </div>

            <div className="negative">
              <span>− Less Discount</span>
              <b>−{money(manualDiscount)}</b>
            </div>

            <div className="subtotal">
              <span>Settlement Balance</span>
              <b>{money(settlementBalance)}</b>
            </div>

            {Object.entries(paymentModes).map(([mode, data]) =>
              data.enabled && n(data.amount) > 0 ? (
                <div key={mode}>
                  <span>
                    {modeConfig[mode].icon} {modeConfig[mode].label}
                  </span>
                  <b>{money(data.amount)}</b>
                </div>
              ) : null
            )}

            <div>
              <span>Total Collected</span>
              <b>{money(totalPaid)}</b>
            </div>

            {applyGST && (
              <>
                <div>
                  <span>GST Number</span>
                  <b>{gstNumber || "—"}</b>
                </div>
                <div>
                  <span>GST 18%</span>
                  <b>+{money(gstAmount)}</b>
                </div>
              </>
            )}

            <div className="final">
              <span>Final Amount Paid</span>
              <b>{money(finalAmount)}</b>
            </div>

            <div className={remainingDue > 0 ? "danger-row" : "success-row"}>
              <span>{remainingDue > 0 ? "⚠️ Remaining Balance" : "✅ Payment Complete"}</span>
              <b>
                {remainingDue > 0
                  ? money(remainingDue)
                  : overPaid > 0
                  ? `Extra ${money(overPaid)}`
                  : "Perfect"}
              </b>
            </div>
          </div>
        </div>

        <div className="aol-paynow-footer">
          <span>Payment confirm will only save settlement. Use separate Freeze button to lock booking.</span>
          <div>
            <button className="aol-light-btn" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button
              className="aol-success-btn"
              onClick={handleSubmit}
              disabled={submitting || totalPaid <= 0 || remainingDue > 0}
            >
              {submitting ? "Saving..." : "✅ Confirm Payment"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════ */
const AdminOrderList = () => {
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [frozenOrders, setFrozenOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(null);
  const [dataSource, setDataSource] = useState("api");
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("timestamp");
  const [sortDir, setSortDir] = useState("desc");
  const [expandedRow, setExpandedRow] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [dbConnected, setDbConnected] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [addonModal, setAddonModal] = useState(null);
  const [payNowModal, setPayNowModal] = useState(null);
  const [freezing, setFreezing] = useState(false);
  const [showFrozen, setShowFrozen] = useState(false);

  const searchRef = useRef(null);
  const filterLabel = formatRangeLabel(fromDate, toDate);

  useEffect(() => {
    const checkHealth = async () => {
      try {
        const res = await fetch(HEALTH_API, {
          signal: AbortSignal.timeout(5000),
        });
        if (!res.ok) throw new Error("Health failed");
        const json = await res.json();
        setDbConnected(json.status === "ok" || json.database === "connected");
      } catch {
        setDbConnected(false);
      }
    };

    checkHealth();
  }, []);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setApiError(null);

    try {
      const params = new URLSearchParams({
        sort: sortKey,
        dir: sortDir,
        limit: "500",
      });

      if (search.trim()) params.set("search", search.trim());

      const res = await fetch(`${ORDERS_API}?${params.toString()}`, {
        signal: AbortSignal.timeout(10000),
      });

      if (!res.ok) throw new Error(`Server responded ${res.status}`);

      const json = await res.json();
      if (json.success === false) throw new Error(json.message || "Fetch failed");

      const data = Array.isArray(json.data) ? json.data : Array.isArray(json) ? json : [];
      const lockedKeys = readFrozenKeys();
      const cachedFrozen = readStoredArray(FROZEN_ORDERS_KEY);
      const frozenCache = new Map(
        cachedFrozen.map((o) => [getOrderStorageKey(o), o]).filter(([key]) => key)
      );

      const normalized = data.map((raw) => {
        const key = getOrderStorageKey(raw);
        const shouldLock = raw?.frozen === true || (key && lockedKeys.has(key));
        const cached = shouldLock && key ? frozenCache.get(key) : null;
        const source = cached ? { ...raw, ...cached } : raw;
        const withTotal = { ...source, total: n(source.total || computeTotal(source)) };
        return shouldLock ? toLockedOrder(withTotal) : withTotal;
      });

      const normalizedFrozen = normalized.filter((o) => isOrderLocked(o, lockedKeys));
      const normalizedNonFrozen = normalized.filter((o) => !isOrderLocked(o, lockedKeys));

      normalizedFrozen.forEach(rememberFrozenLock);

      setOrders(normalizedNonFrozen);
      setFrozenOrders(normalizedFrozen);
      setDataSource("api");
      setDbConnected(true);

      try {
        const existing = readStoredArray(ACTIVE_ORDERS_KEY);
        const idMap = new Map(
          existing.map((o) => [getOrderStorageKey(o) || `${o.name}-${o.timestamp}`, o])
        );

        normalizedNonFrozen.forEach((o) =>
          idMap.set(getOrderStorageKey(o) || `${o.name}-${o.timestamp}`, o)
        );

        writeStoredArray(
          ACTIVE_ORDERS_KEY,
          [...idMap.values()].filter((o) => !isOrderLocked(o))
        );

        const frozenMap = new Map(
          cachedFrozen.map((o) => [getOrderStorageKey(o) || `${o.name}-${o.timestamp}`, o])
        );
        normalizedFrozen.forEach((o) =>
          frozenMap.set(getOrderStorageKey(o) || `${o.name}-${o.timestamp}`, toLockedOrder(o))
        );
        writeStoredArray(FROZEN_ORDERS_KEY, [...frozenMap.values()]);
      } catch {}
    } catch (err) {
      setApiError(err.message);
      setDbConnected(false);

      const lockedKeys = readFrozenKeys();
      const saved = readStoredArray(ACTIVE_ORDERS_KEY);
      const savedFrozen = readStoredArray(FROZEN_ORDERS_KEY);
      const combinedMap = new Map();

      [...saved, ...savedFrozen].forEach((o) => {
        const key = getOrderStorageKey(o) || `${o.name}-${o.timestamp}`;
        if (!key) return;

        const row = isOrderLocked(o, lockedKeys) ? toLockedOrder(o) : o;
        combinedMap.set(key, { ...row, total: n(row.total || computeTotal(row)) });
      });

      const combined = [...combinedMap.values()];
      setOrders(combined.filter((o) => !isOrderLocked(o, lockedKeys)));
      setFrozenOrders(combined.filter((o) => isOrderLocked(o, lockedKeys)).map(toLockedOrder));
      setDataSource("local");
    } finally {
      setLoading(false);
    }
  }, [search, sortDir, sortKey]);

  const fetchMonths = useCallback(async () => {
    try {
      const res = await fetch(`${ORDERS_API}/months`, {
        signal: AbortSignal.timeout(5000),
      });

      if (!res.ok) throw new Error("Months failed");

      const json = await res.json();

      const months = (json.data || [])
        .filter((m) => m._id?.year > 0 && m._id?.month >= 0)
        .map((m) => ({
          year: m._id.year,
          month: m._id.month,
          count: m.count,
          label: `${MONTH_NAMES[m._id.month]} ${m._id.year}`,
        }));

      setAvailableMonths(months);
    } catch {
      const lockedKeys = readFrozenKeys();
      const saved = readStoredArray(ACTIVE_ORDERS_KEY);
      const monthMap = {};

      saved.forEach((o) => {
        if (isOrderLocked(o, lockedKeys)) return;
        const d = parseEventDate(o.date);
        if (!d) return;
        const key = `${d.getFullYear()}-${d.getMonth()}`;
        monthMap[key] = (monthMap[key] || 0) + 1;
      });

      setAvailableMonths(
        Object.entries(monthMap)
          .map(([k, count]) => {
            const [year, month] = k.split("-").map(Number);
            return {
              year,
              month,
              count,
              label: `${MONTH_NAMES[month]} ${year}`,
            };
          })
          .sort((a, b) => b.year - a.year || b.month - a.month)
      );
    }
  }, []);

  useEffect(() => {
    fetchMonths();
  }, [fetchMonths]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const syncLocal = (updater) => {
    try {
      const saved = readStoredArray(ACTIVE_ORDERS_KEY);
      writeStoredArray(
        ACTIVE_ORDERS_KEY,
        updater(saved).filter((o) => !isOrderLocked(o))
      );
    } catch {}
  };

  const guardLockedAction = (order) => {
    if (!isOrderLocked(order)) return false;

    alert("This booking is permanently locked. Admin cannot edit or perform actions on a frozen row.");
    return true;
  };

  const patchOrder = async (order, fields) => {
    if (guardLockedAction(order)) return;

    const id = getOrderId(order);

    setOrders((prev) =>
      prev.map((o) => (getOrderId(o) === id ? { ...o, ...fields } : o))
    );

    syncLocal((saved) =>
      saved.map((o) => (getOrderId(o) === id ? { ...o, ...fields } : o))
    );

    if (id) {
      try {
        const res = await fetch(`${ORDERS_API}/${id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(fields),
          signal: AbortSignal.timeout(8000),
        });

        if (res.ok) {
          const json = await res.json();
          if (json.success && json.data) {
            setOrders((prev) =>
              prev.map((o) => (getOrderId(o) === id ? { ...o, ...json.data } : o))
            );
          }
        } else {
          throw new Error(`Server responded ${res.status}`);
        }
      } catch (err) {
        console.warn("API sync failed:", err.message);
      }
    }
  };

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const displayOrders = showFrozen ? frozenOrders : orders;

  const filtered = useMemo(() => {
    let rows = [...displayOrders];

    const fromD = fromDate ? parseEventDate(fromDate) : null;
    const toD = toDate ? parseEventDate(toDate) : null;

    if (fromD || toD) {
      rows = rows.filter((o) => {
        const ev = parseEventDate(o.date);
        if (!ev) return false;
        if (fromD && ev < fromD) return false;
        if (toD && ev > toD) return false;
        return true;
      });
    }

    if (search.trim()) {
      const q = search.trim().toLowerCase();

      rows = rows.filter((o) =>
        [
          o.name,
          o.theater,
          o.whatsapp,
          o.occasion,
          o.celebrationName,
          o.email,
          o.date,
          o.timeSlot,
        ].some((v) => String(v || "").toLowerCase().includes(q))
      );
    }

    rows.sort((a, b) => {
      let av = a[sortKey] ?? "";
      let bv = b[sortKey] ?? "";

      if (sortKey === "total") {
        av = n(av);
        bv = n(bv);
        return sortDir === "asc" ? av - bv : bv - av;
      }

      return sortDir === "asc"
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });

    return rows;
  }, [displayOrders, fromDate, toDate, search, sortKey, sortDir]);

  const handleStatusUpdate = (order, status) => patchOrder(order, { status });

  const handleSaveAddons = (updatedOrder) => {
    const fields = {
      cakes: updatedOrder.cakes,
      extraDecors: updatedOrder.extraDecors,
      gifts: updatedOrder.gifts,
      services: updatedOrder.services,
      total: computeTotal(updatedOrder),
    };

    return patchOrder(updatedOrder, fields);
  };

  const handlePaymentSubmit = (order, paymentInfo) => {
    const adjustedTotal = computeTotal({
      ...order,
      foodCost: paymentInfo.foodCost,
      manualDiscount: paymentInfo.manualDiscount,
    });

    return patchOrder(order, {
      status: "Fully Paid",
      frozen: false,
      foodCost: paymentInfo.foodCost,
      manualDiscount: paymentInfo.manualDiscount,
      total: adjustedTotal,
      paymentInfo,
    });
  };

  const handleFreeze = async (order) => {
    if (guardLockedAction(order)) return;

    if (!order.paymentInfo) {
      const ok = window.confirm(
        "Payment settlement is not confirmed yet. Do you still want to freeze and permanently lock this booking for admin?"
      );
      if (!ok) return;
    } else {
      const ok = window.confirm(
        "Freeze this booking permanently for admin? After freeze, this row cannot be edited, deleted, paid, printed, unfreezed or used for any admin action from this list."
      );
      if (!ok) return;
    }

    setFreezing(true);
    const id = getOrderId(order);
    const frozenAt = new Date().toISOString();
    const frozenOrder = toLockedOrder({
      ...order,
      frozen: true,
      frozenAt,
      frozenBy: "Admin",
      permanentlyLocked: true,
      lockedAt: frozenAt,
      lockedBy: "Admin",
      status: order.paymentInfo ? "Fully Paid" : order.status || "Advance Paid",
    });

    try {
      let freezeSuccess = false;

      if (id && dataSource === "api") {
        try {
          const res = await fetch(`${ORDERS_API}/${id}/freeze`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              frozen: true,
              frozenBy: "Admin",
              frozenAt,
              permanentlyLocked: true,
              lockedBy: "Admin",
              lockedAt: frozenAt,
            }),
            signal: AbortSignal.timeout(10000),
          });

          if (res.ok) {
            const json = await res.json().catch(() => ({}));
            freezeSuccess = json.success !== false;
          }
        } catch (e) {
          console.warn("Freeze endpoint failed, falling back to PATCH:", e.message);
        }

        if (!freezeSuccess) {
          try {
            const patchRes = await fetch(`${ORDERS_API}/${id}`, {
              method: "PATCH",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                frozen: true,
                status: frozenOrder.status,
                frozenAt,
                frozenBy: "Admin",
                permanentlyLocked: true,
                lockedAt: frozenAt,
                lockedBy: "Admin",
              }),
              signal: AbortSignal.timeout(10000),
            });

            if (patchRes.ok) {
              freezeSuccess = true;
            } else {
              throw new Error(`PATCH responded ${patchRes.status}`);
            }
          } catch (e) {
            console.warn("PATCH freeze failed:", e.message);
          }
        }
      }

      rememberFrozenLock(frozenOrder);
      removeActiveLocal(frozenOrder);
      upsertFrozenLocal(frozenOrder);

      setOrders((prev) => prev.filter((o) => getOrderStorageKey(o) !== getOrderStorageKey(frozenOrder)));

      setFrozenOrders((prev) => {
        const key = getOrderStorageKey(frozenOrder);
        const exists = prev.some((o) => getOrderStorageKey(o) === key);
        if (exists) {
          return prev.map((o) => (getOrderStorageKey(o) === key ? frozenOrder : o));
        }
        return [...prev, frozenOrder];
      });

      alert(
        freezeSuccess
          ? "✅ Order frozen successfully. This row is now permanently locked for admin actions."
          : "✅ Order frozen locally. This row is now permanently locked for admin actions even if server sync failed."
      );

      setExpandedRow(null);
    } catch (err) {
      console.error("Freeze error:", err);
      alert(`Failed to freeze order: ${err.message}`);
    } finally {
      setFreezing(false);
    }
  };

  const handleDelete = async (order) => {
    if (guardLockedAction(order)) return;

    setDeleting(true);

    try {
      const id = getOrderId(order);

      if (id && dataSource === "api") {
        const res = await fetch(`${ORDERS_API}/${id}`, {
          method: "DELETE",
          signal: AbortSignal.timeout(8000),
        });

        if (!res.ok) throw new Error(`Delete failed ${res.status}`);
      }

      syncLocal((saved) => saved.filter((o) => getOrderId(o) !== id));
      setOrders((prev) => prev.filter((o) => getOrderId(o) !== id));
      setDeleteConfirm(null);
      setExpandedRow(null);
    } catch (err) {
      alert(`Delete failed: ${err.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const jumpToMonth = (am) => {
    const y = am.year;
    const m = am.month;
    const lastDay = new Date(y, m + 1, 0).getDate();
    const pad = (x) => String(x).padStart(2, "0");

    setFromDate(`01/${pad(m + 1)}/${y}`);
    setToDate(`${lastDay}/${pad(m + 1)}/${y}`);
    setExpandedRow(null);
  };

  const monthRevenue = filtered.reduce((a, o) => a + n(o.advancePaid || ADVANCE_PAID), 0);
  const monthGrand = filtered.reduce((a, o) => a + n(o.total || computeTotal(o)), 0);
  const totalCollected = filtered.reduce((a, o) => a + n(o.paymentInfo?.totalPaid || 0), 0);

  const isSearchActive = search.trim().length > 0;
  const isDateFiltered = Boolean(fromDate || toDate);

  const statusColors = {
    "Advance Paid": { bg: "#dcfce7", color: "#15803d" },
    "Fully Paid": { bg: "#dbeafe", color: "#1d4ed8" },
    Cancelled: { bg: "#fee2e2", color: "#dc2626" },
    Pending: { bg: "#fef9c3", color: "#a16207" },
    Initiated: { bg: "#fef9c3", color: "#a16207" },
  };

  const Th = ({ k, label }) => (
    <th
      className={`aol-th ${sortKey === k ? "aol-th-active" : ""}`}
      onClick={() => handleSort(k)}
    >
      {label} <span>{sortKey === k ? (sortDir === "asc" ? "↑" : "↓") : "⇅"}</span>
    </th>
  );

  if (loading) {
    return (
      <div className="aol-loading">
        <div className="aol-spinner" />
        <p>Loading orders...</p>
      </div>
    );
  }

  return (
    <div className="aol-page">
      <div className="aol-header">
        <div className="aol-header-left">
          <div className="aol-logo" onClick={() => navigate("/")} style={{ cursor: "pointer" }}>
            🎬
          </div>

          <div>
            <h1>Order Management</h1>
            <p>
              HexaPlay Admin · {filtered.length} booking{filtered.length !== 1 ? "s" : ""}{" "}
              <span className={`aol-live-badge ${dataSource === "api" && dbConnected !== false ? "ok" : "warn"}`}>
                {dataSource === "api" && dbConnected !== false
                  ? "✅ Live MongoDB"
                  : "⚠️ Offline Local"}
              </span>
            </p>
          </div>
        </div>

        <div className="aol-header-right">
          <div className="aol-search-wrap">
            <span>🔍</span>
            <input
              ref={searchRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, theater, phone, occasion..."
            />
            {search && (
              <button
                onClick={() => {
                  setSearch("");
                  searchRef.current?.focus();
                }}
              >
                ✕
              </button>
            )}
          </div>

          <button
            className="aol-refresh-btn"
            onClick={() => {
              fetchOrders();
              fetchMonths();
            }}
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {apiError && (
        <div className="aol-error-banner">
          <span>⚠️ Could not reach server ({apiError}). Showing local data.</span>
          <button onClick={fetchOrders}>Retry</button>
        </div>
      )}

      <div className="aol-filter-bar">
        <div className="aol-date-range-group">
          <strong>📅 Event Date Range</strong>

          <label>
            From{" "}
            <input
              type="date"
              value={toInputVal(fromDate)}
              max={toInputVal(toDate) || undefined}
              onChange={(e) => {
                setFromDate(fromInputVal(e.target.value));
                setExpandedRow(null);
              }}
            />
          </label>

          <span>→</span>

          <label>
            To{" "}
            <input
              type="date"
              value={toInputVal(toDate)}
              min={toInputVal(fromDate) || undefined}
              onChange={(e) => {
                setToDate(fromInputVal(e.target.value));
                setExpandedRow(null);
              }}
            />
          </label>

          {(fromDate || toDate) && (
            <button
              onClick={() => {
                setFromDate("");
                setToDate("");
              }}
            >
              Clear
            </button>
          )}
        </div>

        <div className="aol-month-chips">
          {availableMonths.slice(0, 6).map((am) => (
            <button key={am.label} onClick={() => jumpToMonth(am)}>
              {am.label}
              <b>{am.count}</b>
            </button>
          ))}
        </div>

        <button
          className="aol-download-btn"
          disabled={filtered.length === 0}
          onClick={() => downloadPDF(filtered, filterLabel)}
        >
          🖨️ Download PDF
        </button>
      </div>

      <div className="aol-view-toggle-bar">
        <div className="aol-view-toggle">
          <button
            className={`aol-toggle-btn ${!showFrozen ? "active" : ""}`}
            onClick={() => setShowFrozen(false)}
          >
            📋 Active Orders
            <span className="aol-count-badge">{orders.length}</span>
          </button>
          <button
            className={`aol-toggle-btn ${showFrozen ? "active" : ""}`}
            onClick={() => setShowFrozen(true)}
          >
            🔒 Frozen Orders
            <span className="aol-count-badge frozen">{frozenOrders.length}</span>
          </button>
        </div>

        <div className="aol-view-info">
          {showFrozen
            ? `${frozenOrders.length} frozen booking${frozenOrders.length !== 1 ? "s" : ""}`
            : `${orders.length} active booking${orders.length !== 1 ? "s" : ""}`
          }
        </div>
      </div>

      <div className="aol-stats">
        <div className="aol-stat-card purple">
          <span>📋</span>
          <div>
            <b>{filtered.length}</b>
            <p>{isDateFiltered || isSearchActive ? "Filtered" : showFrozen ? "Frozen" : "Active"} Bookings</p>
          </div>
        </div>

        <div className="aol-stat-card green">
          <span>💰</span>
          <div>
            <b>{money(monthRevenue)}</b>
            <p>Advance Collected</p>
          </div>
        </div>

        <div className="aol-stat-card orange">
          <span>🧾</span>
          <div>
            <b>{money(monthGrand)}</b>
            <p>Total Order Value</p>
          </div>
        </div>

        <div className="aol-stat-card blue">
          <span>✅</span>
          <div>
            <b>{money(totalCollected)}</b>
            <p>Settlement Collected</p>
          </div>
        </div>
      </div>

      <div className="aol-toolbar">
        <span>
          <b>{filtered.length}</b> booking{filtered.length !== 1 ? "s" : ""}
          {showFrozen && " (Frozen)"}
          {isDateFiltered ? (
            <>
              {" "}
              in <b>{filterLabel}</b>
            </>
          ) : null}
          {isSearchActive ? (
            <>
              {" "}
              matching <b>"{search}"</b>
            </>
          ) : null}
        </span>

        <span>← Scroll table horizontally →</span>
      </div>

      <div className="aol-table-outer">
        {filtered.length === 0 ? (
          <div className="aol-empty">
            <div>{isSearchActive ? "🔍" : showFrozen ? "🔒" : "📭"}</div>
            <h3>
              {isSearchActive
                ? "No results found"
                : showFrozen
                ? "No frozen bookings"
                : "No bookings found"}
            </h3>
            <p>
              {isSearchActive
                ? `No orders match "${search}".`
                : isDateFiltered
                ? `No bookings for ${filterLabel}.`
                : showFrozen
                ? "No bookings have been frozen yet. Freeze a booking to lock it permanently."
                : "No bookings recorded yet. Go back and create a booking!"}
            </p>

            {!showFrozen && (
              <button
                className="aol-primary-btn"
                onClick={() => navigate("/")}
                style={{ marginTop: 16 }}
              >
                🎬 Book a Theater
              </button>
            )}
          </div>
        ) : (
          <div className="aol-table-scroll">
            <table className="aol-table">
              <thead>
                <tr>
                  <th className="aol-th">#</th>
                  <Th k="timestamp" label="Booked On" />
                  <Th k="name" label="Customer" />
                  <Th k="theater" label="Theater" />
                  <Th k="date" label="Event Date" />
                  <th className="aol-th">Slot</th>
                  <Th k="occasion" label="Occasion" />
                  <th className="aol-th">Guests</th>
                  <Th k="total" label="Amount" />
                  <th className="aol-th">Status</th>
                  <th className="aol-th">Add-ons</th>
                  <th className="aol-th action">Actions</th>
                </tr>
              </thead>

              <tbody>
                {filtered.map((o, i) => {
                  const id = getOrderId(o) || `${i}`;
                  const addons = buildAddonRows(o);
                  const isExpanded = expandedRow === i;
                  const isFrozen = isOrderLocked(o);
                  const total = n(o.total || computeTotal(o));
                  const sc =
                    statusColors[o.status || "Advance Paid"] || statusColors["Advance Paid"];

                  return (
                    <React.Fragment key={id}>
                      <tr
                        className={`aol-row ${isExpanded ? "expanded" : ""} ${
                          isFrozen ? "frozen" : ""
                        }`}
                        onClick={() => setExpandedRow(isExpanded ? null : i)}
                      >
                        <td>
                          <span className="aol-row-num">{i + 1}</span>
                          {isFrozen && <span title="Frozen"> 🔒</span>}
                        </td>

                        <td>
                          <span className="aol-date-ts">{formatTS(o.timestamp)}</span>
                        </td>

                        <td>
                          <div className="aol-customer">
                            <div className="aol-avatar">
                              {(o.name || "?")[0].toUpperCase()}
                            </div>
                            <div>
                              <b>{o.name || "—"}</b>
                              <span>{o.whatsapp || "—"}</span>
                              {o.celebrationName && <small>🎉 {o.celebrationName}</small>}
                            </div>
                          </div>
                        </td>

                        <td>
                          <span className="aol-theater-pill">{o.theater || "—"}</span>
                        </td>

                        <td>
                          <b>{o.date || "—"}</b>
                        </td>

                        <td>{o.timeSlot || "—"}</td>

                        <td>
                          <span className="aol-occasion-pill">{o.occasion || "—"}</span>
                        </td>

                        <td>
                          <span>👤 {n(o.adults)}</span>
                          {n(o.kids) > 0 && <span> 🧒 {n(o.kids)}</span>}
                          {n(o.glassesCount) > 0 && <span> 🥽 {n(o.glassesCount)}</span>}
                        </td>

                        <td>
                          <div className="aol-amount-cell">
                            <b>{money(total)}</b>
                            <span>{money(o.advancePaid || ADVANCE_PAID)} advance</span>
                            {o.paymentInfo && (
                              <small>
                                Settled {money(o.paymentInfo.totalPaid || o.paymentInfo.finalAmount)}
                              </small>
                            )}
                          </div>
                        </td>

                        <td onClick={(e) => e.stopPropagation()}>
                          {isFrozen ? (
                            <span className="aol-frozen-badge">🔒 Frozen</span>
                          ) : (
                            <select
                              value={o.status || "Advance Paid"}
                              onChange={(e) => handleStatusUpdate(o, e.target.value)}
                              style={{ background: sc.bg, color: sc.color }}
                            >
                              <option>Advance Paid</option>
                              <option>Fully Paid</option>
                              <option>Pending</option>
                              <option>Initiated</option>
                              <option>Cancelled</option>
                            </select>
                          )}
                        </td>

                        <td>
                          {addons.length === 0 ? (
                            <span className="aol-no-addon">—</span>
                          ) : (
                            <div className="aol-addon-chips">
                              {addons.slice(0, 2).map((a) => (
                                <span key={`${a.name}-${a.category}`}>
                                  {a.emoji} {a.name}{safeQty(a) > 1 ? ` x${safeQty(a)}` : ""}
                                </span>
                              ))}
                              {addons.length > 2 && <b>+{addons.length - 2}</b>}
                            </div>
                          )}
                        </td>

                        <td onClick={(e) => e.stopPropagation()}>
                          <div className="aol-actions">
                            {isFrozen ? (
                              <span className="aol-locked-actions" title="Admin actions are permanently locked for this row.">
                                🔒 Permanently Locked
                              </span>
                            ) : (
                              <>
                                <a
                                  className="wa"
                                  href={`https://wa.me/${String(o.whatsapp || "").replace(
                                    /\D/g,
                                    ""
                                  )}?text=${buildWAMessage(o)}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  WhatsApp
                                </a>

                                <button className="addon" onClick={() => setAddonModal(o)}>
                                  ＋ Add-ons
                                </button>

                                <button className="print" onClick={() => printRowPDF(o)}>
                                  Print
                                </button>

                                <button className="pay" onClick={() => setPayNowModal(o)}>
                                  💳 Pay
                                </button>

                                <button
                                  className="freeze"
                                  onClick={() => handleFreeze(o)}
                                  disabled={freezing}
                                >
                                  {freezing ? "Freezing..." : "🔒 Freeze"}
                                </button>

                                <button className="delete" onClick={() => setDeleteConfirm(o)}>
                                  Delete
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>

                      {isExpanded && (
                        <tr className="aol-expand-row">
                          <td colSpan="12">
                            <div className="aol-expand-body">
                              {isFrozen && (
                                <div className="aol-frozen-banner">
                                  🔒 <b>This booking is permanently locked.</b>{" "}
                                  Admin cannot edit this row or perform any action from the order list.
                                  {o.frozenAt && (
                                    <span> · Frozen on {formatTS(o.frozenAt)}</span>
                                  )}
                                </div>
                              )}

                              <div className="aol-expand-grid">
                                <InfoPanel
                                  title="👤 Customer Details"
                                  rows={[
                                    ["Full Name", o.name],
                                    ["Celebrating", o.celebrationName || "—"],
                                    ["WhatsApp", o.whatsapp],
                                    ["Email", o.email || "—"],
                                    ["Order ID", id],
                                  ]}
                                />

                                <InfoPanel
                                  title="🏟️ Booking Details"
                                  rows={[
                                    ["Theater", o.theater],
                                    ["Event Date", o.date],
                                    ["Time Slot", o.timeSlot],
                                    ["Occasion", o.occasion],
                                    [
                                      "Guests",
                                      `${n(o.adults)} Adults${
                                        n(o.kids) ? `, ${n(o.kids)} Kids` : ""
                                      }`,
                                    ],
                                    ["Booked On", formatTS(o.timestamp)],
                                    ["Frozen", isFrozen ? `Yes (${formatTS(o.frozenAt)})` : "No"],
                                  ]}
                                />

                                <PricePanel order={o} />
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {addonModal && !isOrderLocked(addonModal) && (
        <AddAddonsModal
          order={addonModal}
          onClose={() => setAddonModal(null)}
          onSave={handleSaveAddons}
        />
      )}

      {payNowModal && !isOrderLocked(payNowModal) && (
        <PayNowModal
          order={payNowModal}
          onClose={() => setPayNowModal(null)}
          onSubmit={(info) => handlePaymentSubmit(payNowModal, info)}
        />
      )}

      {deleteConfirm && !isOrderLocked(deleteConfirm) && (
        <div className="aol-modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="aol-delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="aol-delete-icon">🗑️</div>
            <h3>Delete this booking?</h3>
            <p>
              You are about to permanently remove <b>{deleteConfirm.name}</b>'s booking
              for <b>{deleteConfirm.theater}</b> on <b>{deleteConfirm.date}</b>.
            </p>

            <div>
              <button
                className="aol-light-btn"
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className="aol-danger-btn"
                onClick={() => handleDelete(deleteConfirm)}
                disabled={deleting}
              >
                {deleting ? "Deleting..." : "Yes, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const InfoPanel = ({ title, rows }) => (
  <div className="aol-expand-section" style={{ backgroundColor: '#fff', border: '1px solid #f1f5f9', borderRadius: '12px', padding: '16px', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.05)' }}>
    <h4 style={{ color: '#1e293b', marginBottom: '16px', fontSize: '15px' }}>{title}</h4>
    {rows.map(([label, value]) => (
      <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f8fafc', color: '#64748b', fontSize: '14px' }}>
        <span>{label}</span>
        <b style={{ color: '#334155', textAlign: 'right', maxWidth: '60%' }}>{value || "—"}</b>
      </div>
    ))}
  </div>
);

// ════════════════════════════════════════════════════════════════
// 100% EXACT REPLICATION OF YOUR PRICING BREAKDOWN IMAGE WITH INLINE CSS
// ════════════════════════════════════════════════════════════════
const PricePanel = ({ order }) => {
  const addons = buildAddonRows(order);
  const total = n(order.total || computeTotal(order));

  return (
    <div style={{ backgroundColor: '#fff', border: '1px solid #f1f5f9', borderRadius: '12px', padding: '24px', boxShadow: '0 4px 15px rgba(0, 0, 0, 0.03)', width: '100%', boxSizing: 'border-box' }}>
      
      <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#1f2937', margin: '0 0 20px 0', fontSize: '16px', fontWeight: 'bold' }}>
        💰 Pricing Breakdown
      </h4>

      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', color: '#6b7280', fontSize: '14px' }}>
        <span>Base Price</span>
        <b style={{ color: '#374151', fontWeight: '700' }}>{money(order.basePrice)}</b>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', color: '#6b7280', fontSize: '14px' }}>
        <span>Fixed Decor</span>
        <b style={{ color: '#374151', fontWeight: '700' }}>{money(order.fixedDecor || DEFAULT_FIXED_DECOR)}</b>
      </div>

      {n(order.extraAdults) > 0 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', color: '#6b7280', fontSize: '14px' }}>
          <span>Extra Adults ({n(order.extraAdults)})</span>
          <b style={{ color: '#374151', fontWeight: '700' }}>{money(n(order.extraAdults) * n(order.extraAdultPrice || DEFAULT_EXTRA_ADULT_PRICE))}</b>
        </div>
      )}

      {n(order.kids) > 0 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', color: '#6b7280', fontSize: '14px' }}>
          <span>Kids ({n(order.kids)})</span>
          <b style={{ color: '#374151', fontWeight: '700' }}>{money(n(order.kids) * n(order.kidPrice || DEFAULT_KID_PRICE))}</b>
        </div>
      )}

      {addons.map((a) => (
        <div key={`${a.category}-${a.name}`} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', color: '#6b7280', fontSize: '14px' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            {a.emoji} {a.name}{safeQty(a) > 1 ? ` x${safeQty(a)}` : ""}
          </span>
          <b style={{ color: '#374151', fontWeight: '700' }}>{money(itemTotal(a))}</b>
        </div>
      ))}

      {n(order.discount) > 0 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', color: '#6b7280', fontSize: '14px' }}>
          <span>Coupon Discount</span>
          <b style={{ color: '#ef4444', fontWeight: '700' }}>-{money(order.discount)}</b>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '18px 0 10px', marginTop: '10px', borderTop: '1px solid #e5e7eb', color: '#374151', fontSize: '14px', fontWeight: '600' }}>
        <span>Grand Total</span>
        <b style={{ color: '#3b82f6', fontSize: '16px', fontWeight: '700' }}>{money(total)}</b>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0 0', color: '#6b7280', fontSize: '14px' }}>
        <span>Advance Paid</span>
        <b style={{ color: '#10b981', fontWeight: '700' }}>{money(order.advancePaid || ADVANCE_PAID)}</b>
      </div>

      {order.paymentInfo && (
        <div style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px dashed #e5e7eb' }}>
          <h5 style={{ margin: '0 0 10px 0', color: '#1f2937', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>💳 Settlement Details</h5>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', color: '#6b7280', fontSize: '13px' }}>
            <span>Actual Balance</span>
            <b style={{ color: '#374151' }}>{money(order.paymentInfo.actualBalance || 0)}</b>
          </div>

          {order.paymentInfo.foodCost > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', color: '#6b7280', fontSize: '13px' }}>
              <span>Food Cost</span>
              <b style={{ color: '#374151' }}>+{money(order.paymentInfo.foodCost || 0)}</b>
            </div>
          )}

          {order.paymentInfo.manualDiscount > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', color: '#6b7280', fontSize: '13px' }}>
              <span>Less Discount</span>
              <b style={{ color: '#ef4444' }}>-{money(order.paymentInfo.manualDiscount || 0)}</b>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', color: '#6b7280', fontSize: '13px' }}>
            <span>Settlement Balance</span>
            <b style={{ color: '#374151' }}>{money(order.paymentInfo.settlementBalance || 0)}</b>
          </div>

          {order.paymentInfo.modes?.map((m) => (
            <div key={m.mode} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', color: '#6b7280', fontSize: '13px' }}>
              <span>Collected via {m.mode}</span>
              <b style={{ color: '#374151' }}>{money(m.amount)}</b>
            </div>
          ))}

          {order.paymentInfo.gstApplied && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', color: '#6b7280', fontSize: '13px' }}>
                <span>GST (18%) - {order.paymentInfo.gstNumber}</span>
                <b style={{ color: '#374151' }}>+{money(order.paymentInfo.gstAmount)}</b>
              </div>
            </>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0 0', marginTop: '6px', borderTop: '1px solid #f1f5f9', color: '#374151', fontSize: '13px', fontWeight: '600' }}>
            <span>Final Amount Settled</span>
            <b style={{ color: '#374151', fontWeight: '700' }}>{money(order.paymentInfo.finalAmount)}</b>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrderList;